<template>
    <!-- 右侧功能区 -->
    <div class="w-80 bg-white border-l border-gray-200 flex flex-col">
        <div class="p-4 border-b border-gray-200">
            <h3 class="font-medium">热点问题</h3>
        </div>
        
        <!-- 相关问题列表 -->
        <div class="flex-1 overflow-y-auto p-4">
            <div v-if="loading" class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span class="ml-2 text-gray-500">加载中...</span>
            </div>
            
            <div v-else-if="relatedQuestions.length === 0" class="text-center py-8">
                <i class="fas fa-question-circle text-4xl text-gray-300 mb-2"></i>
                <p class="text-gray-500">暂无相关问题</p>
            </div>
            
            <div v-else class="space-y-3">
                <div 
                    v-for="question in relatedQuestions" 
                    :key="question.id"
                    class="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    @click="handleQuestionClick(question.content)"
                >
                    <div class="text-sm font-medium mb-1 text-gray-800 line-clamp-2">
                        {{ question.content }}
                    </div>
                    <div class="text-xs text-gray-500">
                        {{ formatDate(question.createdTime) }}
                    </div>
                </div>
            </div>
        </div>
        
        <!-- 底部操作按钮 -->
        <div class="p-4 border-t border-gray-200">
            <div class="space-y-2">
                <button 
                    class="w-full px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors duration-200"
                    @click="exportChatHistory"
                >
                    <i class="fas fa-download mr-2"></i>
                    导出对话记录
                </button>
                <button 
                    class="w-full px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors duration-200"
                    @click="shareChat"
                >
                    <i class="fas fa-share-alt mr-2"></i>
                    分享对话
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import request from '@/utils/request'

export default {
    name: 'Right',
    data() {
        return {
            relatedQuestions: [],
            loading: false
        }
    },
    mounted() {
        this.loadHotQuestions()
    },
    methods: {
        // 仅当字符串包含Unicode转义时进行解码（避免对明文相关问题重复处理）
        decodeUnicodeIfEncoded(str) {
            if (!str || typeof str !== 'string') return ''
            if (/\\u[0-9a-fA-F]{4}/.test(str)) {
                try {
                    return str.replace(/\\u[\dA-Fa-f]{4}/g, (match) => {
                        return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
                    })
                } catch (e) {
                    return str
                }
            }
            return str
        },

        // 标准化文本用于去重：优先用解码后的文本
        normalizeContent(str) {
            return this.decodeUnicodeIfEncoded(str || '')
        },

        // 加载热点问题，失败或不足时回退最新相关问题
        async loadHotQuestions() {
            try {
                this.loading = true
                // 优先热点问题
                let list = []
                try {
                    const token = localStorage.getItem('token') || ''
                    const resp = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/related-questions/hot`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { 'Authorization': token } : {})
                        }
                    })
                    if (resp.ok) {
                        const hot = await resp.json()
                        if (hot && hot.code === 200 && Array.isArray(hot.data)) {
                            list = hot.data || []
                        }
                    } else {
                        // 静默失败，走回退逻辑
                        console.warn('热点问题接口返回非200，status=', resp.status)
                    }
                } catch (e) {
                    console.warn('加载热点问题异常，回退到最新相关问题')
                }

                // 回退：不足5条或无数据，拉取最新相关问题补足
                if (!list || list.length < 5) {
                    try {
                        const latest = await request.get('/api/related-questions/latest')
                        if (latest.data && latest.data.code === 200 && Array.isArray(latest.data.data)) {
                            const latestList = latest.data.data || []
                            // 合并并去重（按“解码后的content”去重，避免收藏的Unicode与相关问题明文重复）
                            const seen = new Set((list || []).map(x => this.normalizeContent(x.content)))
                            latestList.forEach(item => {
                                if (item && item.content) {
                                    const key = this.normalizeContent(item.content)
                                    if (!seen.has(key)) {
                                        list.push(item)
                                        seen.add(key)
                                    }
                                }
                            })
                        }
                    } catch (e) {
                        console.warn('加载最新相关问题失败')
                    }
                }

                // 统一在最终展示前将内容标准化为“可读文本”（仅对Unicode进行解码）并再做一次去重与截断
                const normalized = []
                const seenFinal = new Set()
                for (const item of (list || [])) {
                    if (!item || !item.content) continue
                    const content = this.normalizeContent(item.content)
                    if (seenFinal.has(content)) continue
                    // 输出对象保留原结构，只替换用于展示的content
                    normalized.push({ ...item, content })
                    seenFinal.add(content)
                    if (normalized.length >= 5) break
                }
                this.relatedQuestions = normalized
            } catch (error) {
                console.error('加载热点问题失败:', error)
                this.relatedQuestions = []
            } finally {
                this.loading = false
            }
        },
        
        // 处理问题点击
        handleQuestionClick(questionContent) {
            // 向父组件发送事件，将问题内容传递给聊天组件
            this.$emit('question-clicked', questionContent)
        },
        
        // 格式化日期
        formatDate(dateString) {
            if (!dateString) return ''
            
            try {
                const date = new Date(dateString)
                const now = new Date()
                const diffTime = Math.abs(now - date)
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                
                if (diffDays === 1) {
                    return '今天'
                } else if (diffDays === 2) {
                    return '昨天'
                } else if (diffDays <= 7) {
                    return `${diffDays - 1}天前`
                } else {
                    return date.toLocaleDateString('zh-CN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    })
                }
            } catch (error) {
                console.error('日期格式化失败:', error)
                return dateString
            }
        },
        
        // 导出对话记录
        exportChatHistory() {
            // 向父组件发送导出事件
            this.$emit('export-chat-history')
        },
        
        // 分享对话
        shareChat() {
            // 向父组件发送分享事件
            this.$emit('share-chat')
        }
    }
}
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>